import { GraphQLFaceIdOverviewRequester } from '@cobliteam/api-lib';
import type { DriverFaceIdRegistration, FaceIdCoverageChart, FleetIdentificationHealth } from '@cobliteam/api-lib/src/dashboard/camera/GraphQLFaceIdOverviewRequester';
import { keepPreviousData, type UseQueryOptions, useQueries, useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

export type { FaceIdCoverageGranularity, FleetIdentificationHealth, FleetIdentificationHealthState } from '@cobliteam/api-lib/src/dashboard/camera/GraphQLFaceIdOverviewRequester';

export type FaceIdOverviewState = 'ONBOARDING' | 'ALL_RESOLVED' | 'NORMAL';

const DRIVERS_COUNT_QUERY_KEY = 'FACE-ID-DRIVERS-COUNT';
const COMPLETE_REGISTRATION_COUNT_QUERY_KEY = 'FACE-ID-COMPLETE-REGISTRATION-COUNT';
const UNNAMED_CLUSTERS_COUNT_QUERY_KEY = 'FACE-ID-UNNAMED-CLUSTERS-COUNT';
const IDENTIFICATION_HISTORY_QUERY_KEY = 'FACE-ID-IDENTIFICATION-HISTORY';
const VEHICLES_WITHOUT_DRIVER_QUERY_KEY = 'FACE-ID-VEHICLES-RUNNING-WITHOUT-DRIVER';
const COVERAGE_QUERY_KEY = 'FACE-ID-COVERAGE-CHART';
const REGISTRATIONS_QUERY_KEY = 'FACE-ID-DRIVER-REGISTRATIONS';
const IDENTIFICATION_HEALTH_QUERY_KEY = 'FACE-ID-IDENTIFICATION-HEALTH';

/** Upstream caps `driverFaceIdRegistrations` at a small page; ~200 ids 414s the URL. Stay well under. */
const REGISTRATIONS_CHUNK_SIZE = 50;

// Lazy instantiation to avoid circular dependency issues (same pattern as useDriverFaceCluster).
const getRequester = () => new GraphQLFaceIdOverviewRequester();

const chunk = <T>(items: T[], size: number): T[][] => {
  const chunks: T[][] = [];
  for (let i = 0; i < items.length; i += size) {
    chunks.push(items.slice(i, i + size));
  }
  return chunks;
};

/**
 * KPIs the overview state machine reads. Each is fetched by its own query and therefore arrives
 * independently, so every field is optional: `undefined` means "not resolved yet".
 */
export type FaceIdOverviewKpis = {
  hasFaceIdIdentificationHistory?: boolean;
  driversCount?: number;
  driversWithCompleteFaceIdRegistrationCount?: number;
  unnamedFaceClustersCount?: number;
  vehiclesRunningWithoutDriverCount?: number;
};

/**
 * Derives the overview tab state machine from the per-KPI queries (PRD §3.1.1).
 *
 * Each KPI is fetched separately, so values trickle in. The gating flag
 * `hasFaceIdIdentificationHistory` decides Onboarding; while it is still `undefined` the tab stays
 * `NORMAL` (the caller renders skeletons). `ALL_RESOLVED` only fires once every KPI it depends on
 * has resolved — until then it stays `NORMAL` so the success banner never flashes mid-load.
 */
export const getFaceIdOverviewState = ({
  hasFaceIdIdentificationHistory,
  driversCount,
  driversWithCompleteFaceIdRegistrationCount,
  unnamedFaceClustersCount,
  vehiclesRunningWithoutDriverCount,
}: FaceIdOverviewKpis): FaceIdOverviewState => {
  if (hasFaceIdIdentificationHistory === false) return 'ONBOARDING';
  if (hasFaceIdIdentificationHistory === undefined) return 'NORMAL';

  if (
    unnamedFaceClustersCount === undefined ||
    vehiclesRunningWithoutDriverCount === undefined ||
    driversWithCompleteFaceIdRegistrationCount === undefined ||
    driversCount === undefined
  ) {
    return 'NORMAL';
  }

  const allResolved = unnamedFaceClustersCount === 0 && vehiclesRunningWithoutDriverCount === 0 && driversWithCompleteFaceIdRegistrationCount === driversCount;

  return allResolved ? 'ALL_RESOLVED' : 'NORMAL';
};

/** Total active drivers in the fleet. */
export const useDriversCount = (groupIds?: string[], options?: Partial<UseQueryOptions<number>>) => {
  return useQuery<number>({
    queryKey: [DRIVERS_COUNT_QUERY_KEY, groupIds],
    queryFn: () => getRequester().getDriversCount({ groupIds }),
    staleTime: 300_000,
    refetchOnWindowFocus: false,
    ...options,
  });
};

/** Drivers with a complete Face-ID registration (numerator of the registration KPI). */
export const useDriversWithCompleteFaceIdRegistrationCount = (groupIds?: string[], options?: Partial<UseQueryOptions<number>>) => {
  return useQuery<number>({
    queryKey: [COMPLETE_REGISTRATION_COUNT_QUERY_KEY, groupIds],
    queryFn: () => getRequester().getDriversWithCompleteFaceIdRegistrationCount({ groupIds }),
    staleTime: 300_000,
    refetchOnWindowFocus: false,
    ...options,
  });
};

/** Unnamed face clusters awaiting identification. */
export const useUnnamedFaceClustersCount = (groupIds?: string[], options?: Partial<UseQueryOptions<number>>) => {
  return useQuery<number>({
    queryKey: [UNNAMED_CLUSTERS_COUNT_QUERY_KEY, groupIds],
    queryFn: () => getRequester().getUnnamedFaceClustersCount({ groupIds }),
    staleTime: 300_000,
    refetchOnWindowFocus: false,
    ...options,
  });
};

/** Gating flag for the Onboarding vs Normal/All-resolved state machine. */
export const useHasFaceIdIdentificationHistory = (groupIds?: string[], options?: Partial<UseQueryOptions<boolean>>) => {
  return useQuery<boolean>({
    queryKey: [IDENTIFICATION_HISTORY_QUERY_KEY, groupIds],
    queryFn: () => getRequester().getHasFaceIdIdentificationHistory({ groupIds }),
    staleTime: 300_000,
    refetchOnWindowFocus: false,
    ...options,
  });
};

/** Slow KPI split from `useFaceIdOverview` so the four fast cards don't wait for it. */
export const useVehiclesRunningWithoutDriverCount = (groupIds?: string[], options?: Partial<UseQueryOptions<number>>) => {
  return useQuery<number>({
    queryKey: [VEHICLES_WITHOUT_DRIVER_QUERY_KEY, groupIds],
    queryFn: () => getRequester().getVehiclesRunningWithoutDriverCount({ groupIds }),
    staleTime: 300_000,
    refetchOnWindowFocus: false,
    ...options,
  });
};

/**
 * Fleet identification-method health that drives the "saúde do método" tag. Fleet-scoped (resolved from
 * the session at the BFF), so unlike the count KPIs it takes no `groupIds` and ignores the group filter.
 * Resolves to `null` when the backend has no health data yet.
 */
export const useFleetIdentificationHealth = (options?: Partial<UseQueryOptions<FleetIdentificationHealth | null>>) => {
  return useQuery<FleetIdentificationHealth | null>({
    queryKey: [IDENTIFICATION_HEALTH_QUERY_KEY],
    queryFn: () => getRequester().getFleetIdentificationHealth(),
    staleTime: 300_000,
    refetchOnWindowFocus: false,
    ...options,
  });
};

export const useFaceIdCoverageChart = (fromTimestamp: number, toTimestamp: number, groupIds?: string[], options?: Partial<UseQueryOptions<FaceIdCoverageChart>>) => {
  return useQuery<FaceIdCoverageChart>({
    queryKey: [COVERAGE_QUERY_KEY, fromTimestamp, toTimestamp, groupIds],
    queryFn: () => getRequester().getFaceIdCoverageChart({ fromTimestamp, toTimestamp, groupIds }),
    staleTime: 300_000,
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData,
    ...options,
  });
};

export type UseDriverFaceIdRegistrationsResult = {
  /** Authoritative registration per driver. Drivers absent here have no registration (treat as incomplete). */
  registrationsByDriverId: Map<string, DriverFaceIdRegistration>;
  isLoading: boolean;
  isError: boolean;
};

/**
 * Fetches Face-ID registration status for every driver id, chunked to respect the upstream page cap,
 * and aggregates the chunks into one map. `combine` keeps the aggregate referentially stable across renders.
 */
export const useDriverFaceIdRegistrations = (driverIds: string[], options?: { enabled?: boolean }): UseDriverFaceIdRegistrationsResult => {
  const enabled = options?.enabled ?? true;
  const chunks = useMemo(() => chunk(driverIds, REGISTRATIONS_CHUNK_SIZE), [driverIds]);

  return useQueries({
    queries: chunks.map((ids) => ({
      queryKey: [REGISTRATIONS_QUERY_KEY, ids],
      queryFn: () => getRequester().getDriverFaceIdRegistrations({ driverIds: ids }),
      staleTime: 300_000,
      refetchOnWindowFocus: false,
      enabled: enabled && ids.length > 0,
    })),
    combine: (results) => {
      const registrationsByDriverId = new Map<string, DriverFaceIdRegistration>();
      for (const result of results) {
        for (const registration of result.data ?? []) {
          registrationsByDriverId.set(registration.driverId, registration);
        }
      }
      return {
        registrationsByDriverId,
        isLoading: results.some((result) => result.isLoading),
        isError: results.some((result) => result.isError),
      };
    },
  });
};
